import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    IconButton,
    MenuItem,
    Button,
    Tooltip,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import RichEditorResultText from './Shared/RichEditorResultText';
import RichEditor from './Shared/RichEditor';
import {useGetBoarderGroupsQuery} from '../../../../../api/services/groupService';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const BoarderStatusChoice = ({
                                 onChange,
                                 index,
                                 componentId,
                                 fieldKey,
                                 options,
                                 value,
                                 descriptionHtml,
                                 onDescriptionHtmlChange,
                                 commitDescriptionHtml,
                                 enableValidation,
                                 isEditMode,
                                 isSubmitMode,
                                 isFormPreviewMode,
                                 isFormArchived,
                                 relatedComponent,
                                 accessLevel,
                                 readOnly,
                                 placeholderValues,
                                 error,
                                 audience,
                                 recipientType,
                                 linkedToStudents,
                                 linkedToEmployees,
                                 submitMode
                             }) => {
    const {data: boarderGroups = [], isLoading: loadingBoarderGroups} = useGetBoarderGroupsQuery();
    const noBoarderGroups = !loadingBoarderGroups && boarderGroups.length === 0;
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleOptionChange = (optionIndex, field, newValue) => {
        const updatedOptions = [...options];
        updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            [field]: newValue
        };
        onChange({
            index,
            option: updatedOptions[optionIndex],
            optionId: updatedOptions[optionIndex].id
        });
    };

    const addOption = () => {
        const newOption = {id: Date.now(), description: '', value: ''};
        onChange({
            index,
            isAdd: true,
            option: newOption
        });
    };

    const removeOption = (optionIndex) => {
        const optionToRemove = options[optionIndex];
        onChange({
            index,
            isRemove: true,
            optionId: optionToRemove.id
        });
    };

    const formatBoarderGroupDisplay = (group) => `${group.name}`;

    const renderEditMode = () => (
        <Box display="flex" flexDirection="column" sx={{mt: 2}}>
            <Box>
                <RichEditor
                    currentDescription={draftDescriptionHtml}
                    setCurrentDescription={handleDraftChange}
                    isFormLive={isFormArchived}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    placeholderValues={placeholderValues}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                {noBoarderGroups && (
                    <Alert severity="info" sx={{mt: 2, mb: 2}} data-testid="boarder-status-none">
                        No available boarder status options. Boarder groups are hidden on the School Setup tab of Settings,
                        or the school has none. Remove this question from the form.
                    </Alert>
                )}
                {!noBoarderGroups && options.length === 1 && (
                    <Alert severity="info" sx={{
                        mt: 2,
                        mb: 2
                    }}>
                        With only one option configured, this choice will not be visible in view mode and the
                        option will be automatically selected.
                    </Alert>
                )}
                <Box>
                    {!noBoarderGroups && options.map((option, optionIndex) => (
                        <Box
                            py={1}
                            display="flex"
                            justifyContent="space-between"
                            gap={1}
                            alignItems="center"
                            key={`boarderStatusChoiceOption-${optionIndex}`}
                        >
                            <Typography>{optionIndex + 1}.</Typography>
                            <TextField
                                multiline
                                fullWidth
                                sx={{flex: 2}}
                                value={option.description}
                                disabled={isFormArchived}
                                label={
                                    !option.description
                                        ? 'Enter a choice for your respondents to select'
                                        : null
                                }
                                InputLabelProps={{
                                    shrink: false
                                }}
                                onChange={e =>
                                    handleOptionChange(optionIndex, 'description', e.target.value)
                                }
                            />
                            <TextField
                                select
                                required
                                sx={{flex: 1}}
                                error={
                                    !option?.value ||
                                    (option?.value &&
                                        !boarderGroups.some(x => x.id === option.value))
                                }
                                value={option.value || ''}
                                label="Boarder Group"
                                onChange={e =>
                                    handleOptionChange(
                                        optionIndex,
                                        'value',
                                        Number(e.target.value)
                                    )
                                }
                            >
                                {boarderGroups.map(group => (
                                    <MenuItem key={group.id} value={`${group.id}`}>
                                        {formatBoarderGroupDisplay(group)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Tooltip
                                title={
                                    relatedComponent?.targetState === option.id
                                        ? `This option cannot be deleted as it is currently used in the ${relatedComponent?.relatedFormName} form.`
                                        : null
                                }
                                disabled={isFormArchived}
                            >
                                <IconButton>
                                    <DeleteIcon
                                        onClick={() =>
                                            relatedComponent?.targetState === option.id ||
                                            isFormArchived
                                                ? {}
                                                : removeOption(optionIndex)
                                        }
                                    />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
                {!noBoarderGroups && (
                    <Button
                        onClick={addOption}
                        startIcon={<AddIcon/>}
                        cursor="pointer"
                        disabled={isFormArchived}
                    >
                        Add option
                    </Button>
                )}
            </Box>
            <RequiredCheckbox
                enableValidation={enableValidation}
                index={index}
                onChange={onChange}
                isFormArchived={isFormArchived || !!fieldKey}
            />
        </Box>
    );

    const renderDisplayMode = () => {
        const displayOptions = readOnly
            ? options
            : options.filter(option => {
                const selectedGroup = boarderGroups.find(i => i.id === option.value);
                return selectedGroup !== undefined && selectedGroup !== null;
            });

        if (
            (isFormPreviewMode || isSubmitMode) &&
            !readOnly &&
            (displayOptions.length === 1 || displayOptions.length === 0)
        ) {
            return null;
        }

        return (
            <Box key={`boarderStatusChoice-${index}`} sx={{mt: 2}}>
                <RichEditorResultText
                    id={componentId}
                    description={descriptionHtml}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    enableValidation={enableValidation}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
                <FormControl error={!!error} variant="standard">
                    <RadioGroup
                        value={value || ''}
                        onClick={e =>
                            onChange({
                                id: componentId,
                                radioValue: e.target.value === value ? null : e.target.value
                            })
                        }
                    >
                        {displayOptions.map((option, idx) => (
                            <FormControlLabel
                                key={`boarderStatusChoiceOption-${idx}`}
                                sx={{
                                    '& .MuiTypography-root': {
                                        wordBreak: 'break-word',
                                        maxWidth: '800px'
                                    }
                                }}
                                value={option.id}
                                control={<Radio/>}
                                label={option.description}
                            />
                        ))}
                    </RadioGroup>
                    <FormHelperText>{error}</FormHelperText>
                </FormControl>
            </Box>
        );
    };

    useEffect(() => {
        if (!isEditMode && !readOnly) {
            const displayOptions = options.filter(option => {
                const selectedGroup = boarderGroups.find(i => i.id === option.value);
                return selectedGroup !== undefined && selectedGroup !== null;
            });

            if (displayOptions.length === 1 && !value) {
                onChange({
                    id: componentId,
                    radioValue: displayOptions[0].id
                });
            }
        }
    }, [isEditMode, options, value, boarderGroups, readOnly, componentId, onChange]);

    return isEditMode ? renderEditMode() : renderDisplayMode();
};

BoarderStatusChoice.propTypes = {
    onChange: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    componentId: PropTypes.string.isRequired,
    fieldKey: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            description: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isSubmitMode: PropTypes.bool,
    isFormPreviewMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    accessLevel: PropTypes.number,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default BoarderStatusChoice;
